import type { CurrentUser } from '@/services/backend/api';

const permissionRouteMap = {
  canDashboard: 'dashboard.read',
  canMembers: 'member.read',
  canFreezeMember: 'member.freeze',
  canUpdateMembers: 'member.update',
  canDeleteMembers: 'member.delete',
  canBrokers: 'broker.read',
  canAuditBrokers: 'broker.audit',
  canUpdateBrokers: 'broker.update',
  canFreezeBrokers: 'broker.freeze',
  canDeleteBrokers: 'broker.delete',
  canLeaders: 'leader.read',
  canAuditLeaders: 'leader.audit',
  canUpdateLeaders: 'leader.update',
  canFreezeLeaders: 'leader.freeze',
  canDeleteLeaders: 'leader.delete',
  canStoreManagers: 'store.read',
  canUpdateStoreManagers: 'store.manage',
  canFreezeStoreManagers: 'store.manage',
  canDeleteStoreManagers: 'store.manage',
  canMeetings: 'meeting.read',
  canUpdateMeetings: 'meeting.update',
  canCustomIntents: 'custom_intent.read',
  canUpdateCustomIntents: 'custom_intent.update',
  canOrders: 'order.read',
  canEarnings: 'earning.read',
  canWithdrawals: 'withdrawal.read',
  canReviewWithdrawals: 'withdrawal.review',
  canPayWithdrawals: 'withdrawal.pay',
  canPosts: 'post.read',
  canAuditPosts: 'post.audit',
  canStores: 'store.read',
  canManageStores: 'store.manage',
  canArticles: 'article.read',
  canManageArticles: 'article.manage',
  canPartnerServices: 'partner_service.read',
  canManagePartnerServices: 'partner_service.manage',
  canCompanyStores: 'company_store.read',
  canManageCompanyStores: 'company_store.manage',
  canSettings: 'settings.read',
  canUpdateSettings: 'settings.update',
  canManageOperations: 'operations.manage',
  canReports: 'reporting.read',
  canLogs: 'log.read',
  canManageAdmins: 'admin.manage',
} as const;

type AccessFlags = {
  canAdmin: boolean;
} & Record<keyof typeof permissionRouteMap, boolean>;

export function hasPermission(
  currentUser: Partial<CurrentUser> | undefined,
  permission: string,
) {
  if (currentUser?.primary_role !== 'admin') {
    return false;
  }
  return (currentUser.permissions ?? []).includes(permission);
}

export function buildAccessFlags(
  currentUser: Partial<CurrentUser> | undefined,
): AccessFlags {
  const canAdmin = currentUser?.primary_role === 'admin' && currentUser.account_status === 'active';
  const permissionFlags = Object.entries(permissionRouteMap).reduce(
    (result, [key, permission]) => {
      result[key as keyof typeof permissionRouteMap] = canAdmin
        ? hasPermission(currentUser, permission)
        : false;
      return result;
    },
    {} as Record<keyof typeof permissionRouteMap, boolean>,
  );

  return {
    canAdmin,
    ...permissionFlags,
  };
}
